import React, { useState } from "react";
import Swal from "sweetalert2";
import { useNavigate, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../layouts/PageTitle";
import { createVehicleApi } from "../../../services/vehicle";
import { appApi, useGetCompaniesQuery } from "../../../store/appApi";
import { selectHasModuleAccess } from "../../../store/authSlice";

const AddVehicle = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasVehiclesWrite = useSelector(
    selectHasModuleAccess("vehicles", "read-write"),
  );

  // Company list for the dropdown (vehicles belong to a company)
  // const { data: companiesResponse } = useGetCompaniesQuery();
  // const companies = Array.isArray(companiesResponse?.data)
  //   ? companiesResponse.data
  //   : [];

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    vehicleMake: "",
    vehicleNumber: "",
    vehicleModel: "",
    initialKm: "",
    // companyId: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.vehicleMake.trim()) e.vehicleMake = "Vehicle Make is required.";
    if (!form.vehicleNumber.trim())
      e.vehicleNumber = "Vehicle Number is required.";
    if (!form.vehicleModel.trim())
      e.vehicleModel = "Vehicle Model is required.";
    if (form.initialKm !== "" && Number(form.initialKm) < 0)
      e.initialKm = "Kilometers cannot be negative.";
    // if (!form.companyId) e.companyId = "Company is required.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please fill in all required fields.",
      });
      return;
    }

    try {
      setLoading(true);
      await createVehicleApi({
        vehicleMake: form.vehicleMake,
        vehicleNumber: form.vehicleNumber,
        vehicleModel: form.vehicleModel,
        initialKm: form.initialKm === "" ? 0 : Number(form.initialKm) || 0,
        // companyId: form.companyId,
      });

      // Bust the RTK cache so List.jsx refetches automatically
      dispatch(appApi.util.invalidateTags(["Vehicles"]));

      Swal.fire({
        icon: "success",
        title: "Vehicle Added!",
        text: `${form.vehicleMake} has been added successfully.`,
        confirmButtonText: "Go to Vehicles",
      }).then(() => navigate("/vehicles"));
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to create vehicle. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  if (!hasVehiclesWrite) return <Navigate to="/vehicles" replace />;
  return (
    <>
      <PageTitle activeMenu="Add Vehicle" motherMenu="Vehicles / Machines" />
      <div className="row">
        <div className="col-xl-8 col-lg-10 mx-auto">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Vehicle Information</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Vehicle Make */}
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Vehicle Make <span className="text-danger">*</span>
                      </label>
                      <input
                        name="vehicleMake"
                        type="text"
                        className={`form-control ${errors.vehicleMake ? "is-invalid" : ""}`}
                        placeholder="e.g. Tata, Mahindra"
                        value={form.vehicleMake}
                        onChange={handleChange}
                      />
                      {errors.vehicleMake && (
                        <div className="invalid-feedback">
                          {errors.vehicleMake}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Vehicle Number */}
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Vehicle Number <span className="text-danger">*</span>
                      </label>
                      <input
                        name="vehicleNumber"
                        type="text"
                        style={{ textTransform: "uppercase" }}
                        className={`form-control  ${errors.vehicleNumber ? "is-invalid" : ""}`}
                        placeholder="e.g. MH12AB1234"
                        value={form.vehicleNumber}
                        onChange={handleChange}
                      />
                      {errors.vehicleNumber && (
                        <div className="invalid-feedback">
                          {errors.vehicleNumber}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Vehicle Model */}
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Vehicle Model <span className="text-danger">*</span>
                      </label>
                      <input
                        name="vehicleModel"
                        type="text"
                        className={`form-control ${errors.vehicleModel ? "is-invalid" : ""}`}
                        placeholder="e.g. Ace, Bolero"
                        value={form.vehicleModel}
                        onChange={handleChange}
                      />
                      {errors.vehicleModel && (
                        <div className="invalid-feedback">
                          {errors.vehicleModel}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Initial Kilometers */}
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Initial Kilometers{" "}
                        <span className="text-muted small fw-normal">
                          (already run)
                        </span>
                      </label>
                      <div className="input-group">
                        <input
                          name="initialKm"
                          type="number"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          className={`form-control ${errors.initialKm ? "is-invalid" : ""}`}
                          placeholder="e.g. 45200"
                          value={form.initialKm}
                          onChange={handleChange}
                        />
                        <span className="input-group-text">km</span>
                      </div>
                      {errors.initialKm ? (
                        <div className="invalid-feedback">
                          {errors.initialKm}
                        </div>
                      ) : (
                        <small className="text-muted d-block">
                          Odometer reading at the time of adding. Leave blank if
                          new (0).
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Company */}
                  {/* <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Company <span className="text-danger">*</span>
                      </label>
                      <select
                        name="companyId"
                        className={`form-control ${errors.companyId ? "is-invalid" : ""}`}
                        value={form.companyId}
                        onChange={handleChange}
                      >
                        <option value="">Select Company</option>
                        {companies.map((c) => (
                          <option key={c._id || c.id} value={c._id || c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      {errors.companyId && (
                        <div className="invalid-feedback">
                          {errors.companyId}
                        </div>
                      )}
                    </div>
                  </div> */}

                  {/* Actions */}
                  <div className="col-12 mt-2">
                    <button
                      type="submit"
                      className="btn btn-primary me-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Adding…
                        </>
                      ) : (
                        "Add Vehicle"
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger light"
                      onClick={() => navigate("/vehicles")}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddVehicle;
